import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DashboardLayout from "../components/DashboardLayout";
import BrandDashboardLayout from "../components/BrandDashboardLayout";
import { toast } from "sonner";
import { ArrowLeft, Send, Check, X } from "lucide-react";

export default function Conversation() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [body, setBody] = useState("");
  const scrollRef = useRef(null);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    api.get(`/conversations/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Conversation not found"))
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data?.messages?.length]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/conversations/${id}/messages`, { body });
      setBody("");
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const decide = async (decision) => {
    try {
      await api.post(`/conversations/${id}/decision`, { decision, note: "" });
      toast.success(decision === "accepted" ? "Partnership accepted" : "Partnership declined");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const isBrand = user?.account_type === "brand";
  const Layout = isBrand ? BrandDashboardLayout : DashboardLayout;

  if (loading) return <Layout><div className="p-12 text-ink-muted">Loading…</div></Layout>;
  if (!data) return <Layout><div className="p-12 text-ink-muted">Conversation not found.</div></Layout>;

  const c = data.conversation;
  const viewerRole = data.viewer_role; // brand | athlete | agent | system
  const canDecide = (viewerRole === "athlete" || viewerRole === "agent") && c.status === "open";
  const counterpart = isBrand ? data.athlete_display : data.brand_display;
  const counterpartMeta = isBrand ? data.athlete_sport : data.brand_category;

  return (
    <Layout>
      <div className="p-6 md:p-12 max-w-3xl flex flex-col" style={{ minHeight: "calc(100vh - 100px)" }}>
        <button onClick={() => navigate("/app/inbox")} className="text-xs uppercase tracking-widest text-ink-muted hover:text-white inline-flex items-center gap-2 mb-6" data-testid="convo-back-btn">
          <ArrowLeft className="w-3 h-3" /> Inbox
        </button>

        <div className="surface p-5 mb-4 flex flex-wrap justify-between items-start gap-3" data-testid="convo-header">
          <div className="min-w-0">
            <div className="label-eyebrow">{isBrand ? "Athlete" : "Brand"}</div>
            <div className="font-heading text-2xl uppercase tracking-tight mt-1" data-testid="convo-counterpart">{counterpart}</div>
            {counterpartMeta && <div className="text-xs text-ink-muted">{counterpartMeta}</div>}
            <div className="text-sm text-white mt-3" data-testid="convo-subject"><span className="text-ink-muted">Subject: </span>{c.subject}</div>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-ink-muted">
              {c.budget_indication && <span><span className="text-ink-muted">Budget: </span><span className="text-white">{c.budget_indication}</span></span>}
              {c.campaign_window && <span><span className="text-ink-muted">Window: </span><span className="text-white">{c.campaign_window}</span></span>}
            </div>
          </div>
          <StatusBadge status={c.status} />
        </div>

        {canDecide && (
          <div className="flex gap-2 mb-4">
            <button onClick={() => decide("accepted")} className="btn-volt text-sm inline-flex items-center gap-2 flex-1" data-testid="accept-partnership-btn">
              <Check className="w-4 h-4" /> Accept partnership
            </button>
            <button onClick={() => decide("declined")} className="btn-ghost-line text-sm inline-flex items-center gap-2 flex-1" data-testid="decline-partnership-btn">
              <X className="w-4 h-4" /> Decline
            </button>
          </div>
        )}

        <div ref={scrollRef} className="surface p-5 flex-1 overflow-y-auto space-y-4 min-h-[300px]" data-testid="convo-messages">
          {data.messages.map(m => <Message key={m.id} m={m} viewerRole={viewerRole} />)}
        </div>

        {c.status !== "declined" && c.status !== "closed" ? (
          <div className="surface p-4 mt-4">
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={3}
              placeholder="Type your reply…" data-testid="convo-input"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm resize-none" />
            <div className="flex justify-end mt-3">
              <button onClick={send} disabled={sending || !body.trim()} className="btn-volt text-sm inline-flex items-center gap-2 disabled:opacity-40" data-testid="convo-send-btn">
                <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        ) : (
          <div className="surface p-4 mt-4 text-center text-xs text-ink-muted uppercase tracking-widest">This conversation is {c.status}</div>
        )}
      </div>
    </Layout>
  );
}

function StatusBadge({ status }) {
  const map = {
    open: { txt: "Open", cls: "border-volt text-volt" },
    accepted: { txt: "Accepted", cls: "border-[#34C759] text-[#34C759]" },
    declined: { txt: "Declined", cls: "border-[#FF3B30] text-[#FF3B30]" },
    closed: { txt: "Closed", cls: "border-line text-ink-muted" },
  };
  const s = map[status] || map.open;
  return <span className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${s.cls} h-fit shrink-0`} data-testid="convo-status">{s.txt}</span>;
}

function Message({ m, viewerRole }) {
  const isSystem = m.from_role === "system";
  const isMine = m.from_role === viewerRole || (viewerRole === "agent" && m.from_role === "athlete");
  if (isSystem) {
    return <div className="text-center text-[10px] uppercase tracking-widest text-ink-muted py-2">{m.body}</div>;
  }
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div className={`max-w-[80%] p-3 text-sm whitespace-pre-wrap ${isMine ? "bg-volt/10 border border-volt/30 text-white" : "bg-bg-elev border border-line text-ink"}`}>
        <div className="text-[9px] uppercase tracking-widest mb-1.5 opacity-60">{m.from_role}</div>
        {m.body}
      </div>
    </div>
  );
}
